/**
 * v2 API contract of «Люди» (/people, /people/:id; docs/research/design2-records.md §4, §8 R2, R3,
 * R7, R10, R12, R14, R16, R17; decisions-log V6, V7, V15, V18, V32): the list, the card with terms,
 * assignments and their pay, history, and the inline writes.
 */
import { z } from 'zod';
import { isIsoDate, isPeriod } from '../dates';
import { CURRENCIES } from '../money';
import { isRateFormat } from '../calc/prices';
import type { PayFormat, PaySource } from '../calc/prices';
import type { CardOption } from './projects';

export const PERSON_CARD_STAGES = ['candidate', 'onboarding', 'active', 'offboarding', 'left'] as const;
export type PersonCardStage = (typeof PERSON_CARD_STAGES)[number];

export const PERSON_KINDS = ['contractor', 'employee'] as const;
export type PersonKind = (typeof PERSON_KINDS)[number];

/** `billed_hourly` (V184): the person's rate × the hours billed to the client. */
export const ASSIGNMENT_PAY_FORMATS = ['hourly', 'billed_hourly', 'monthly', 'scope'] as const;
/** Formats of a pay period of an assignment (V69, V73): the three above, shares and the salary. */
export const PAY_PERIOD_FORMATS = ['hourly', 'billed_hourly', 'monthly', 'scope', 'revenue_share', 'profit_share', 'salary'] as const;

export interface PersonListRow {
  readonly id: string;
  readonly name: string;
  readonly location: string | null;
  readonly kind: PersonKind;
  /** Stage as shown: offboarding turns into «Ушёл» after the last assignment ends (V18). */
  readonly stage: PersonCardStage;
  /** «Архив» tab: left or archived (R10). */
  readonly archived: boolean;
  readonly since: string | null;
  /** Tenure runs from `since` to this date (R7). */
  readonly tenureEnd: string;
  /** Distinct projects with an assignment not ended before today (R10). */
  readonly activeProjects: number;
}

export interface PeopleListResponse {
  readonly rows: readonly PersonListRow[];
}

/** Terms or own pay from a date. */
export interface PayRateView {
  readonly effectiveFrom: string;
  readonly format: PayFormat;
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly currency: string;
}

export interface ResolvedPayView extends PayRateView {
  readonly shareE6?: number | null;
  readonly issuerId?: string | null;
  readonly source: PaySource;
}

/** The project's share of the employee's taxes for the tax month (R14). */
export interface AssignmentTaxShare {
  readonly totalMinor: number;
  readonly incomeMinor: number;
  readonly currency: string;
}

/** How the salary through our entity meets the project (V257): no amounts, only the way. */
export interface AssignmentCreditView {
  readonly issuerId: string;
  readonly issuerName: string;
  /** `credited`: a rated project the salary is credited against (V145); `rest`: a «Зарплата через юрлицо» project taking what is left. */
  readonly kind: 'credited' | 'rest';
}

export interface PersonAssignmentRow {
  readonly assignmentId: string;
  readonly projectId: string;
  readonly projectName: string;
  readonly from: string | null;
  readonly to: string | null;
  /** Ended before today: the row is dimmed and read-only. */
  readonly isEnded: boolean;
  /** Pay for the current month, or the start month of a future assignment (R3). */
  readonly pay: ResolvedPayView | null;
  /** Entities whose salary is credited against this project's pay (V145); «Изменить» opens with them ticked (V255). */
  readonly creditEntityIds: readonly string[];
  /** How the salary through our entities meets this project (V257); empty when it does not. */
  readonly credits: readonly AssignmentCreditView[];
  readonly tax: AssignmentTaxShare | null;
  /** The assignment's own pay by month (V73), newest first. */
  readonly payPeriods: readonly PayPeriodView[];
  /** The first month a new pay period can start: after the last paid month, not before the start. */
  readonly openFrom: string;
}

/**
 * A pay period of an assignment (V73): from the 1st of a month until the next one. It is closed once
 * every month it covers is paid (read-only, «закрыта»), editable while no month it covers is paid.
 */
export interface PayPeriodView {
  readonly id: string;
  /** `YYYY-MM`. */
  readonly from: string;
  /** The last month before the next period; null = open-ended. */
  readonly to: string | null;
  readonly format: PayFormat;
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly shareE6: number | null;
  readonly issuerId: string | null;
  readonly issuerName: string | null;
  readonly currency: string;
  /** Every month it covers is paid. */
  readonly closed: boolean;
  /** No month it covers is paid: the period can be changed or removed. */
  readonly editable: boolean;
}

/** POST /records/assignments/:id/pay-periods — a pay period from the 1st of `from` (V73). */
export const payPeriodSchema = z
  .object({
    from: z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ'),
    format: z.enum(PAY_PERIOD_FORMATS),
    rateE4: z.number().int().positive().nullable().default(null),
    amountMinor: z.number().int().positive().nullable().default(null),
    /** Shares; salary (V151): the project's percent of the salary, credited to it. */
    shareE6: z.number().int().positive().max(1_000_000, 'Доля — до 100 %').nullable().default(null),
    issuerId: z.string().min(1).nullable().default(null),
    currency: z.enum(CURRENCIES),
    /** Takes back the approval of the months the period changes (V138). */
    confirm: z.boolean().optional(),
  })
  .refine((b) => !isRateFormat(b.format) || b.rateE4 != null, { message: 'Укажите ставку в час', path: ['rateE4'] })
  .refine((b) => (b.format !== 'monthly' && b.format !== 'scope') || b.amountMinor != null, { message: 'Укажите сумму', path: ['amountMinor'] })
  .refine((b) => (b.format !== 'revenue_share' && b.format !== 'profit_share') || b.shareE6 != null, { message: 'Укажите процент', path: ['shareE6'] })
  .refine((b) => b.format !== 'salary' || b.issuerId != null, { message: 'Укажите юрлицо', path: ['issuerId'] });
export type PayPeriodBody = z.output<typeof payPeriodSchema>;
export type PayPeriodInput = z.input<typeof payPeriodSchema>;

export interface PersonTaxRates {
  readonly entityId: string;
  readonly entityName: string;
  readonly incomeBp: number | null;
  readonly pensionBp: number | null;
  readonly employerBp: number | null;
}

/** «Изменения ставок» (R16): own assignment pay, newest first. */
/** A step of the person's own pay on a project (V267): in force from `from` until `to` (null = still). */
export interface PersonProjectRateStep {
  readonly from: string;
  readonly to: string | null;
  readonly rate: PayRateView & { readonly shareE6: number | null; readonly issuerId: string | null };
  /** The entity of a «Зарплата через юрлицо» step; null otherwise. */
  readonly issuerName: string | null;
}

/** «История» → a project of the person (V267): every assignment, ended ones too, with its rate steps newest first. */
export interface PersonProjectHistory {
  readonly assignmentId: string;
  readonly projectId: string;
  readonly projectName: string;
  /** Null for an assignment without a start date. */
  readonly from: string | null;
  readonly to: string | null;
  readonly isEnded: boolean;
  readonly rates: readonly PersonProjectRateStep[];
}

/** A step of the salary through an entity (V267): the оклад or the amount on hand, and the advance. */
export interface PersonSalaryStep {
  readonly from: string;
  readonly to: string | null;
  readonly grossMinor: number | null;
  readonly netMinor: number;
  readonly advanceMinor: number;
  readonly currency: string;
  readonly contractNumber: string | null;
}

/** «История» → an entity the person was paid a salary through (V267), with the salary steps newest first. */
export interface PersonEntityHistory {
  readonly issuerId: string;
  readonly issuerName: string;
  readonly from: string;
  /** The last day of the salary (the day before the 0 row); null while it goes on. */
  readonly to: string | null;
  readonly salaries: readonly PersonSalaryStep[];
}

/** A salary event or a leave on «История» (V281): what the entity's labour rules know of beside the salary itself. */
export type PersonLeaveKind = 'vacation' | 'unpaid' | 'sick' | 'bonus' | 'compensation' | 'aid' | 'severance';

/**
 * «Отпуска и события» of the person card (V281): a salary event through an entity (vacation pay, sick
 * pay, a bonus, the compensation for unused vacation, unpaid leave) or a leave entered on projects
 * alone (V234), newest first.
 */
export interface PersonLeaveView {
  readonly key: string;
  readonly period: string;
  readonly kind: PersonLeaveKind;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  /** Calendar days of a leave, the days typed for an unpaid leave without dates; null for a bonus or the aid. */
  readonly days: number | null;
  /** On hand; 0 for unpaid leave, null for a leave without a salary or when not computable. */
  readonly amountMinor: number | null;
  readonly currency: string | null;
  /** The entity whose salary the event is on; null for a leave on projects only. */
  readonly issuerName: string | null;
  /** The projects the leave covers (V234); empty for an event entered on «План выплат». */
  readonly projectNames: readonly string[];
  readonly approved: boolean;
}

export type PersonPayoutStatus = 'planned' | 'approved' | 'paid';

/** «Выплаты» (R17): one row per month × currency. */
export interface PersonPayoutView {
  readonly period: string;
  readonly currency: string;
  readonly clientNames: readonly string[];
  readonly amountMinor: number;
  readonly status: PersonPayoutStatus;
}

export interface PersonCard {
  readonly id: string;
  readonly name: string;
  readonly kind: PersonKind;
  readonly stage: PersonCardStage;
  /** The stored stage (the select edits it). */
  readonly storedStage: PersonCardStage;
  readonly location: string | null;
  readonly since: string | null;
  readonly employerIssuerId: string | null;
  readonly employerName: string | null;
  /** IBAN of the default bank account. */
  readonly iban: string | null;
  /** «Оформлен в штат» (V252): one block per entity the person is or was employed in; empty = never employed. */
  readonly employments: readonly PersonEmploymentView[];
  /** Assignments: active first, then by start, newest first. */
  readonly assignments: readonly PersonAssignmentRow[];
  /** Month of the employee tax column (the first open month). */
  readonly taxPeriod: string;
  readonly contractCount: number;
  /** «История» (V267): the projects with their rate steps and the entities with their salary steps. */
  readonly projectHistory: readonly PersonProjectHistory[];
  readonly entityHistory: readonly PersonEntityHistory[];
  readonly payouts: readonly PersonPayoutView[];
  /** «Отпуска и события» (V281): the salary events and the leaves, newest first. */
  readonly leaves: readonly PersonLeaveView[];
  readonly entityOptions: readonly CardOption[];
  /** «Зарплата через юрлицо» (V56): every salary row, newest first. */
  readonly salaries: readonly PersonSalaryView[];
  /** Projects a salary can be bound to: the person's assignments' projects. */
  readonly salaryProjectOptions: readonly CardOption[];
  readonly today: string;
  readonly nextMonthStart: string;
}

/** One part of an employee's taxes (V256): a rate or a deduction and the month's amount. */
export interface PersonTaxPart {
  readonly key: 'income' | 'deduction_standard' | 'deduction_young' | 'pension' | 'employer';
  readonly title: string;
  /** Basis points of a rate; null for a deduction. */
  readonly rateBp: number | null;
  /** A deduction's amount (0 when it does not apply this month), or the rate's amount in the month; null without a salary month. */
  readonly amountMinor: number | null;
}

/** «Налоги» of an employment (V256, V263): every part of the percent with the figures of the salary in force for a full month. */
export interface PersonTaxBreakdown {
  /** The month whose year gives the deduction figures. */
  readonly period: string;
  readonly currency: string;
  /** The salary's gross and on hand for a full month; null without a salary through the entity. */
  readonly grossMinor: number | null;
  readonly netMinor: number | null;
  readonly parts: readonly PersonTaxPart[];
}

/** The paid vacation of an employment (V252): 24 calendar days a year earned by whole months (ст. 155 ТК РБ), less the days taken. */
export interface PersonVacationView {
  readonly earnedDays: number;
  readonly usedDays: number;
  /** Earned less used; negative when more was taken than earned. */
  readonly leftDays: number;
}

/** The salary in force through the entity today (V252): the оклад and on hand, or on hand as typed. */
export interface PersonEmploymentSalary {
  readonly grossMinor: number | null;
  readonly netMinor: number;
  readonly advanceMinor: number;
  readonly currency: string;
}

/**
 * «Оформлен в штат» on the person card (V252): the entity, since when, by which contract, the salary
 * in force, the entity's taxes and the vacation left. Read-only; «Оформление / увольнение из юрлица»
 * in «Зафиксировать» changes it.
 */
export interface PersonEmploymentView {
  readonly issuerId: string;
  readonly issuerName: string;
  /** First day of the current employment: the first salary row of its run, else the contract's start, else «Работает с». */
  readonly since: string | null;
  readonly contractId: string | null;
  readonly contractNumber: string | null;
  /** The salary in force today, or the last one before the dismissal; null = none. */
  readonly salary: PersonEmploymentSalary | null;
  readonly taxRates: PersonTaxRates | null;
  /** The taxes decomposed (V256); null when the entity has no rates. */
  readonly taxBreakdown: PersonTaxBreakdown | null;
  /** Молодой специалист (V256): the deduction holds until this date (null = until told otherwise); absent when not one. */
  readonly youngSpecialist: { readonly until: string | null } | null;
  /** Null when the entity has no labour rules (V72); counted up to the dismissal when there is one. */
  readonly vacation: PersonVacationView | null;
  /** The last working day of a dismissal (V175, V254); null while employed. */
  readonly dismissedOn: string | null;
}

/** A salary row: on hand per month through our entity from a date; 0 ends it (V56). */
export interface PersonSalaryView {
  readonly id: string;
  readonly effectiveFrom: string;
  readonly issuerId: string;
  readonly issuerName: string;
  /** Projects the salary is credited against (V145); empty = every project of the person. */
  readonly projectIds: readonly string[];
  readonly projectNames: readonly string[];
  /** On hand: typed, or the оклад less the entity's withheld taxes (V238). */
  readonly netMinor: number;
  /** Оклад by the employment contract (V238); null = the row was typed on hand. */
  readonly grossMinor: number | null;
  /** «№ 3» of the contract the row comes from; null = typed on «Оформление». */
  readonly contractNumber: string | null;
  /** Part of the net paid as an advance (V71); 0 = none. */
  readonly advanceMinor: number;
  readonly currency: string;
}

/**
 * POST /records/people/:id/salaries — a row with the same date, entity and first project is
 * replaced; PUT /records/salaries/:id — the same body rewrites that row. `projectIds` empty = every
 * project of the person (V145).
 */
export const personSalarySchema = z
  .object({
    effectiveFrom: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД'),
    issuerId: z.string().min(1, 'Укажите юрлицо'),
    projectIds: z.array(z.string().min(1)).max(50).default([]),
    netMinor: z.number().int().min(0, 'Сумма — число от 0'),
    /** Оклад by the employment contract (V238): the net then follows the entity's withheld taxes. */
    grossMinor: z.number().int().positive().nullable().default(null),
    advanceMinor: z.number().int().min(0, 'Аванс — число от 0').default(0),
    currency: z.enum(CURRENCIES),
  })
  .strict()
  .refine((b) => b.advanceMinor <= b.netMinor, { message: 'Аванс больше зарплаты', path: ['advanceMinor'] });
export type PersonSalaryBody = z.output<typeof personSalarySchema>;
export type PersonSalaryInput = z.input<typeof personSalarySchema>;

const positiveInt = z.number().int().positive();

const priceRefine = (body: { format: string; rateE4?: number; amountMinor?: number }): boolean =>
  isRateFormat(body.format) ? body.rateE4 != null && body.amountMinor == null : body.amountMinor != null && body.rateE4 == null;
const PRICE_MESSAGE = 'Почасовой — ставка, иначе сумма';

/** Own pay of an assignment from its first unpaid month (R12, V7, V138); `confirm` as above. */
export const assignmentPayTodaySchema = z
  .object({
    format: z.enum(ASSIGNMENT_PAY_FORMATS),
    rateE4: positiveInt.optional(),
    amountMinor: positiveInt.optional(),
    currency: z.enum(CURRENCIES),
    confirm: z.boolean().optional(),
  })
  .refine(priceRefine, PRICE_MESSAGE);
export type AssignmentPayTodayBody = z.infer<typeof assignmentPayTodaySchema>;

const PERSON_PATCH_FIELDS = ['fullName', 'status', 'kind', 'employerIssuerId', 'location', 'startedOn', 'iban'] as const;
const MAX_TEXT = 300;

/** One field per request (inline field pattern, V32). */
export const personPatchSchemaV2 = z
  .object({
    /** The person's name (V171). */
    fullName: z.string().trim().min(1, 'Имя не может быть пустым').max(MAX_TEXT).optional(),
    status: z.enum(PERSON_CARD_STAGES).optional(),
    kind: z.enum(PERSON_KINDS).optional(),
    employerIssuerId: z.string().min(1).nullable().optional(),
    location: z.string().trim().max(MAX_TEXT).nullable().optional(),
    startedOn: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД').nullable().optional(),
    iban: z.string().trim().max(MAX_TEXT).nullable().optional(),
  })
  .strict()
  .refine((body) => PERSON_PATCH_FIELDS.filter((key) => body[key] !== undefined).length === 1, 'Измените одно поле');
export type PersonPatchBodyV2 = z.infer<typeof personPatchSchemaV2>;
